import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { OpportunityProvider } from './contexts/OpportunityContext';
import { SavedOpportunitiesProvider } from './contexts/SavedOpportunitiesContext';
import { NotificationProvider } from './contexts/NotificationContext';
import Notifications from './pages/Notifications';
import Navigation from './components/Navigation';
import Footer from './components/Footer';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import RegionGuard from './components/RegionGuard';
import Landing from './pages/Landing';
import About from './pages/About';
import Contact from './pages/Contact';
import Blog from './pages/Blog';
import BlogArticle from './pages/BlogArticle';
import Login from './pages/Login';
import Signup from './pages/Signup';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import Home from './pages/Home';
import Contracts from './pages/Contracts';
import Insights from './pages/Insights';
import Account from './pages/Account';
import Profile from './pages/Profile';
import Preferences from './pages/Preferences';
import Admin from './pages/Admin';
import StripeSuccess from './pages/StripeSuccess';
import Terms from './pages/Terms';
import Privacy from './pages/Privacy';
import OpportunityDetail from './pages/OpportunityDetail';
import SavedOpportunities from './pages/SavedOpportunities';
import SavedOpportunityToast from './components/SavedOpportunityToast';
import BlogAdmin from './pages/BlogAdmin';
import { DEFAULT_REGION } from './regions';
import { initAnalytics, trackPageView } from './utils/analytics';

function AnalyticsTracker() {
  const location = useLocation();

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    trackPageView(`${location.pathname}${location.search}`);
  }, [location]);

  return null;
}

const RootOpportunitiesRedirect: React.FC = () => {
  const location = useLocation();
  return <Navigate to={`/${DEFAULT_REGION}/opportunities${location.search}`} replace />;
};

const RegionContractsRedirect: React.FC = () => {
  const { region } = useParams();
  const location = useLocation();
  const targetRegion = region ?? DEFAULT_REGION;
  return <Navigate to={`/${targetRegion}/opportunities${location.search}`} replace />;
};

function App() {
  return (
    <AuthProvider>
      <NotificationProvider>
      <OpportunityProvider>
        <Router>
          <SavedOpportunitiesProvider>
            <AnalyticsTracker />
            <div className="flex min-h-screen flex-col bg-gray-50">
              <Navigation />
              <div className="flex-1">
                <Routes>
                {/* Global landing page (no region prefix) */}
                <Route path="/" element={<Landing />} />

                {/* Global public routes */}
                <Route path="/about" element={<About />} />
                <Route path="/blog" element={<Blog />} />
                <Route path="/blog/:slug" element={<BlogArticle />} />
                <Route path="/contact" element={<Contact />} />
                <Route path="/insights" element={<Insights />} />
                <Route path="/terms" element={<Terms />} />
                <Route path="/privacy" element={<Privacy />} />
                <Route path="/login" element={<Login />} />
                <Route path="/signup" element={<Signup />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/notifications" element={<ProtectedRoute><Layout><Notifications /></Layout></ProtectedRoute>} />

                {/* Stripe callback routes */}
                <Route
                  path="/stripe/success"
                  element={(
                    <ProtectedRoute>
                      <Layout>
                        <StripeSuccess />
                      </Layout>
                    </ProtectedRoute>
                  )}
                />

                {/* Global protected routes */}
                <Route
                  path="/account"
                  element={(
                    <ProtectedRoute>
                      <Layout>
                        <Account />
                      </Layout>
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/profile"
                  element={(
                    <ProtectedRoute>
                      <Layout>
                        <Profile />
                      </Layout>
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/preferences"
                  element={(
                    <ProtectedRoute>
                      <Layout>
                        <Preferences />
                      </Layout>
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/grandslam"
                  element={(
                    <ProtectedRoute>
                      <Layout>
                        <Admin />
                      </Layout>
                    </ProtectedRoute>
                  )}
                />
                <Route
                  path="/grandslam/blog"
                  element={(
                    <ProtectedRoute>
                      <BlogAdmin />
                    </ProtectedRoute>
                  )}
                />
                <Route path="/admin" element={<Navigate to="/grandslam" replace />} />

                {/* Legacy redirects to region default */}
                <Route path="/home" element={<Navigate to={`/${DEFAULT_REGION}/home`} replace />} />
                <Route path="/contracts" element={<RootOpportunitiesRedirect />} />
                <Route path="/opportunities" element={<RootOpportunitiesRedirect />} />

                {/* Region-specific routes */}
                <Route element={<RegionGuard />}>
                  <Route path="/:region" element={<Landing />} />
                  <Route
                    path="/:region/opportunities"
                    element={(
                      <Layout>
                        <Contracts />
                      </Layout>
                    )}
                  />
                  <Route
                    path="/:region/opportunities/:id"
                    element={(
                      <Layout>
                        <OpportunityDetail />
                      </Layout>
                    )}
                  />
                  <Route
                    path="/:region/saved"
                    element={(
                      <ProtectedRoute>
                        <Layout>
                          <SavedOpportunities />
                        </Layout>
                      </ProtectedRoute>
                    )}
                  />
                  <Route path="/:region/contracts" element={<RegionContractsRedirect />} />
                  <Route
                    path="/:region/home"
                    element={(
                      <Layout>
                        <Home />
                      </Layout>
                    )}
                  />
                </Route>

                {/* Catch-all */}
                <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </div>
              <Footer />
              <SavedOpportunityToast />
            </div>
          </SavedOpportunitiesProvider>
        </Router>
      </OpportunityProvider>
      </NotificationProvider>
    </AuthProvider>
  );
}

export default App;
